import {
  CrownOutlined,
  LogoutOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Avatar, Button, Divider, Dropdown, Modal, Popover, message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { openPageInNewTab } from '../../public/openPageInNewTab'
import { PUBLIC_LEGAL_ROUTES, type PublicLegalRouteKey } from '../../public/routes'
import LanguageSwitcher from '../../components/LanguageSwitcher'
import { useAuth } from '../AuthContext'
import { retryTransientOnce } from '../authErrorPolicy'
import AccountSwitchModal, {
  type AccountCredentialModalMode,
} from '../accountSwitch/AccountSwitchModal'
import {
  clearCrossAccountClientState,
  executeAccountSwitch,
  notifyOtherTabsAccountSwitched,
  suppressUnloadPromptsForAccountSwitch,
} from '../accountSwitch/accountSwitchLifecycle'
import {
  executeCanvasGuardedSwitch,
  prepareCanvasForAccountSwitch,
} from '../accountSwitch/canvasAccountSwitchGuard'
import {
  loadDeviceAccounts,
  removeDeviceAccountFromCache,
  setDeviceAccounts,
} from '../accountSwitch/deviceAccountsStore'
import { useDeviceAccounts } from '../accountSwitch/useDeviceAccounts'
import {
  fetchDeviceAccounts,
  removeDeviceAccount,
  type DeviceAccountOut,
  type SaasApiError,
} from '../api'
import {
  ensureActiveCanvasReturnContext,
  resolveCanvasExitBackofficeRoute,
  type CanvasExitBackofficeDestination,
} from '../navigation/canvasNavigationContext'
import ThemeToggle from '../ui/ThemeToggle'
import { exitCanvasToBackoffice, useCanvasWorkspaceTransition } from '../workspace/canvasWorkspaceTransition'
import { useMembershipFlow } from '../workspace/contentProduction/canvas/membership/membershipFlowContext'
import '../workspace/contentProduction/canvas/global-account-entry.css'

export type TenantAccountMenuSurface = 'canvas' | 'workbench'

export interface TenantAccountMenuProps {
  workspaceId: string
  surface: TenantAccountMenuSurface
  /** Required when `surface === 'canvas'`. */
  contentProjectId?: string
}

function initialsFrom(displayName: string | undefined, email: string | undefined): string {
  const source = (displayName || '').trim() || (email || '').trim()
  if (!source) return '?'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return source.slice(0, 2).toUpperCase()
}

export default function TenantAccountMenu({ workspaceId, surface, contentProjectId = '' }: TenantAccountMenuProps) {
  const { me, logout, switchAccount } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('accountEntry')
  const { openMembership, setRestoreTarget } = useMembershipFlow()
  const { phase, requestExitToWorkspace } = useCanvasWorkspaceTransition()
  const [open, setOpen] = useState(false)
  const [switchingUserId, setSwitchingUserId] = useState<string | null>(null)
  const [credentialOpen, setCredentialOpen] = useState(false)
  const [credentialMode, setCredentialMode] = useState<AccountCredentialModalMode>('ADD_ACCOUNT_LOGIN')
  const [reauthEmail, setReauthEmail] = useState('')
  const [reauthUserId, setReauthUserId] = useState<string | null>(null)
  const [canvasPromptOpen, setCanvasPromptOpen] = useState(false)
  const [pendingUserId, setPendingUserId] = useState<string | null>(null)

  const currentWorkspace = useMemo(
    () => me?.workspaces.find((w) => w.workspace_id === workspaceId) ?? me?.workspaces[0],
    [me, workspaceId],
  )

  const currentUserId = me?.user.id
  // Shared across every page's menu (VistoeraTASK-00562): loaded on open only,
  // de-duplicated, never polled, owned by the signed-in user id.
  const deviceAccounts = useDeviceAccounts(currentUserId)
  const accountsStatus = deviceAccounts.status

  const otherAccounts = useMemo(
    () =>
      deviceAccounts.accounts.filter((account) => !account.is_current && account.user_id !== currentUserId),
    [deviceAccounts.accounts, currentUserId],
  )

  const loadAccounts = useCallback(
    async (force = false) => {
      if (!currentUserId) return
      await loadDeviceAccounts(
        currentUserId,
        async () => (await retryTransientOnce(fetchDeviceAccounts)).data.accounts,
        { force },
      )
    },
    [currentUserId],
  )

  useEffect(() => {
    if (!open || !currentUserId) return
    void loadAccounts()
  }, [open, currentUserId, loadAccounts])

  const close = () => setOpen(false)

  const openAccountPage = (path: string) => {
    close()
    if (surface === 'workbench') {
      navigate(path)
      return
    }
    openPageInNewTab(path)
  }

  const goToAccountDestination = (destination: CanvasExitBackofficeDestination) => {
    close()
    const route = resolveCanvasExitBackofficeRoute(workspaceId, destination)
    if (surface === 'workbench') {
      navigate(route)
      return
    }
    if (phase !== 'idle') return
    ensureActiveCanvasReturnContext(workspaceId, contentProjectId, 'direct-url')
    exitCanvasToBackoffice(requestExitToWorkspace, navigate, route)
  }

  const openLegalPage = (key: PublicLegalRouteKey) => {
    close()
    openPageInNewTab(PUBLIC_LEGAL_ROUTES[key])
  }

  const goToInline = (path: string) => {
    close()
    navigate(path)
  }

  const onLogout = async () => {
    close()
    await logout()
    navigate('/login', { replace: true, state: null })
  }

  const finishSwitch = async (userId: string) => {
    setSwitchingUserId(userId)
    // Capture where the user is BEFORE the switch (path only: query/hash may hold
    // old-account ids/filters and are dropped).
    const fromPathname = location.pathname
    try {
      // VistoeraTASK-00563 — server switch, then clear old-identity state, then ONE
      // full page load on the mapped page (see `executeAccountSwitch`). No in-SPA
      // identity swap, so the old workspace id is never used with the new session.
      await executeAccountSwitch(userId, fromPathname, {
        callSwitchApi: (id) => retryTransientOnce(() => switchAccount(id)),
        clearState: clearCrossAccountClientState,
        notifyOtherTabs: notifyOtherTabsAccountSwitched,
        suppressUnloadPrompts: suppressUnloadPromptsForAccountSwitch,
        hardNavigate: (url) => window.location.replace(url),
      })
      close()
      // Success: the document is being replaced; keep the button in its loading state.
    } catch (err) {
      const apiErr = err as SaasApiError
      message.error(apiErr.message || t('switchAccount.switchFailed'))
      setSwitchingUserId(null)
    }
  }

  const requestSwitch = async (account: DeviceAccountOut) => {
    if (account.is_current || switchingUserId) return
    if (account.needs_relogin) {
      close()
      setReauthEmail(account.email)
      setReauthUserId(account.user_id)
      setCredentialMode('REAUTH_REQUIRED')
      setCredentialOpen(true)
      return
    }
    if (!account.can_switch) {
      message.error(t('switchAccount.switchUnavailable'))
      return
    }
    // VistoeraTASK-00581 — canvas is autosaved. Flush under the CURRENT session
    // first; only open the dialog when persistence actually fails (never for a
    // healthy pending/saving debounce that workbench exits ignore).
    setSwitchingUserId(account.user_id)
    try {
      const prep = await prepareCanvasForAccountSwitch()
      if (prep === 'save_failed') {
        setPendingUserId(account.user_id)
        setCanvasPromptOpen(true)
        setSwitchingUserId(null)
        return
      }
      await finishSwitch(account.user_id)
    } catch (err) {
      const apiErr = err as SaasApiError
      message.error(apiErr.message || t('switchAccount.switchFailed'))
      setSwitchingUserId(null)
    }
  }

  const onRemoveAccount = async (account: DeviceAccountOut) => {
    try {
      await removeDeviceAccount(account.user_id)
      // VistoeraTASK-00580 — drop from the shared cache immediately so every
      // TenantAccountMenu (workbench + Canvas) re-renders without waiting for
      // the follow-up GET. Then force-reload so the server list is the SoT;
      // if that GET fails we keep the optimistic removal (DELETE already
      // succeeded) rather than resurrecting the account.
      if (currentUserId) removeDeviceAccountFromCache(currentUserId, account.user_id)
      await loadAccounts(true)
      message.success(t('switchAccount.removeSuccess'))
    } catch (err) {
      const apiErr = err as SaasApiError
      message.error(apiErr.message || t('switchAccount.removeFailed'))
    }
  }

  const openAddAccount = () => {
    close()
    setReauthEmail('')
    setReauthUserId(null)
    setCredentialMode('ADD_ACCOUNT_LOGIN')
    setCredentialOpen(true)
  }

  const onCredentialSuccess = async (data: { accounts: DeviceAccountOut[] }) => {
    if (currentUserId) setDeviceAccounts(currentUserId, data.accounts)
    setCredentialOpen(false)
    if (credentialMode === 'REAUTH_REQUIRED' && reauthUserId) {
      const targetId = reauthUserId
      setReauthUserId(null)
      await finishSwitch(targetId)
      return
    }
    message.success(t('switchAccount.addSuccess'))
  }

  const getPopupContainer = (triggerNode: HTMLElement) => {
    if (surface === 'canvas') {
      return (triggerNode.closest('.pcv-root') as HTMLElement | null) ?? document.body
    }
    return document.body
  }

  const slotClass = surface === 'canvas' ? 'gae-fixed-slot' : 'gae-workbench-slot'

  if (me === null) {
    return (
      <div className={slotClass}>
        <Popover
          trigger="click"
          placement="bottomRight"
          open={open}
          onOpenChange={setOpen}
          overlayClassName="gae-popover"
          getPopupContainer={getPopupContainer}
          content={
            <div className="gae-anonymous">
              <div className="gae-anonymous-title">{t('anonymous.title')}</div>
              <div className="gae-anonymous-subtitle">{t('anonymous.subtitle')}</div>
              <div className="gae-anonymous-actions">
                <button type="button" className="gae-btn gae-btn-primary" onClick={() => goToInline('/login')}>
                  {t('anonymous.login')}
                </button>
                <button type="button" className="gae-btn" onClick={() => goToInline('/signup')}>
                  {t('anonymous.signup')}
                </button>
              </div>
              <Divider style={{ margin: '10px 0' }} />
              <div className="gae-row" role="presentation">
                <span className="gae-row-label">{t('menu.uiLanguage')}</span>
                <LanguageSwitcher />
              </div>
              <div className="gae-row" role="presentation">
                <span className="gae-row-label">{t('menu.appearance')}</span>
                <ThemeToggle />
              </div>
            </div>
          }
        >
          <button type="button" className="gae-avatar-trigger" aria-label={t('avatar.anonymousAriaLabel')}>
            <Avatar size={30} icon={<UserOutlined />} className="gae-avatar gae-avatar-anonymous" />
          </button>
        </Popover>
      </div>
    )
  }

  const user = me?.user
  const isPlatformAdmin = Boolean(me?.is_platform_admin)

  return (
    <div className={slotClass}>
      <Popover
        trigger="click"
        placement="bottomRight"
        open={open}
        onOpenChange={setOpen}
        overlayClassName="gae-popover"
        getPopupContainer={getPopupContainer}
        content={
          <div className="gae-menu" role="menu" aria-label={t('avatar.ariaLabel')}>
            <div className="gae-menu-scroll">
            <div className="gae-header">
              <Avatar size={36} className="gae-avatar">
                {initialsFrom(user?.display_name, user?.email)}
              </Avatar>
              <div className="gae-header-text">
                <div className="gae-header-name">{user?.display_name || user?.email}</div>
                <div className="gae-header-email">{user?.email}</div>
                <button
                  type="button"
                  className="gae-header-workspace"
                  onClick={() => goToAccountDestination('WORKSPACE_OVERVIEW')}
                >
                  {currentWorkspace?.workspace_name || t('menu.workspace')}
                  <RightOutlined className="gae-menu-item-chevron" />
                </button>
              </div>
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div
              className="gae-menu-item gae-menu-item-membership"
              role="menuitem"
              tabIndex={0}
              data-testid="account-menu-become-member"
              onClick={(event) => {
                setRestoreTarget(event.currentTarget)
                close()
                openMembership()
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setRestoreTarget(event.currentTarget)
                  close()
                  openMembership()
                }
              }}
            >
              <CrownOutlined className="gae-menu-item-icon" />
              {t('menu.becomeMember')}
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div className="gae-menu-item" role="menuitem" tabIndex={0} onClick={() => goToAccountDestination('PROFILE')}>
              {t('menu.profile')}
            </div>
            <div
              className="gae-menu-item"
              role="menuitem"
              tabIndex={0}
              onClick={() => goToAccountDestination('WORKSPACE_OVERVIEW')}
            >
              {t('menu.workspace')}
            </div>
            <div className="gae-menu-item" role="menuitem" tabIndex={0} onClick={() => goToAccountDestination('PLAN_USAGE')}>
              {t('menu.planUsage')}
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div
              className="gae-menu-item"
              role="menuitem"
              tabIndex={0}
              onClick={() => openAccountPage(`/workspace/${workspaceId}/account/settings`)}
            >
              {t('menu.settings')}
            </div>
            <div className="gae-row" role="presentation">
              <span className="gae-row-label">{t('menu.uiLanguage')}</span>
              <LanguageSwitcher />
            </div>
            <div className="gae-row" role="presentation">
              <span className="gae-row-label">{t('menu.appearance')}</span>
              <ThemeToggle />
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div className="gae-menu-section-label">{t('menu.legalSupportSection')}</div>
            <div
              className="gae-menu-item"
              role="menuitem"
              tabIndex={0}
              onClick={() => openAccountPage(`/workspace/${workspaceId}/account/help`)}
            >
              {t('menu.help')}
            </div>
            <div className="gae-menu-item" role="menuitem" tabIndex={0} onClick={() => openLegalPage('terms')}>
              {t('menu.terms')}
            </div>
            <div className="gae-menu-item" role="menuitem" tabIndex={0} onClick={() => openLegalPage('privacy')}>
              {t('menu.privacy')}
            </div>
            <div className="gae-menu-item" role="menuitem" tabIndex={0} onClick={() => openLegalPage('billing')}>
              {t('menu.billing')}
            </div>
            <div className="gae-menu-item" role="menuitem" tabIndex={0} onClick={() => openLegalPage('refund')}>
              {t('menu.refund')}
            </div>

            {isPlatformAdmin ? (
              <div className="gae-menu-item" role="menuitem" tabIndex={0} onClick={() => openAccountPage('/platform')}>
                {t('menu.platformAdmin')}
              </div>
            ) : null}

            {accountsStatus === 'error' ? (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <div className="gae-account-switch-error" role="alert" data-testid="account-menu-accounts-error">
                  <span>{t('switchAccount.loadFailed')}</span>
                  <button
                    type="button"
                    className="gae-account-switch-retry"
                    data-testid="account-menu-accounts-retry"
                    onClick={() => void loadAccounts(true)}
                  >
                    {t('switchAccount.retry')}
                  </button>
                </div>
              </>
            ) : null}

            {otherAccounts.length > 0 ? (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <div
                  className="gae-account-switch-list"
                  role="group"
                  aria-label={t('switchAccount.otherAccountsGroup')}
                  data-testid="account-menu-other-accounts"
                >
                  {otherAccounts.map((account) => (
                    <div key={account.user_id} className="gae-account-switch-row">
                      <Avatar size={28} className="gae-avatar gae-account-switch-avatar">
                        {initialsFrom(account.display_name ?? undefined, account.email)}
                      </Avatar>
                      <div className="gae-account-switch-meta">
                        <div className="gae-account-switch-name" title={account.display_name || account.email}>
                          {account.display_name || account.email}
                        </div>
                        {account.display_name ? (
                          <div className="gae-account-switch-email" title={account.email}>
                            {account.email}
                          </div>
                        ) : null}
                      </div>
                      <Button
                        type="primary"
                        size="small"
                        className="gae-account-switch-btn"
                        data-testid={`account-menu-switch-${account.user_id}`}
                        loading={switchingUserId === account.user_id}
                        disabled={Boolean(switchingUserId) && switchingUserId !== account.user_id}
                        onClick={(event) => {
                          event.stopPropagation()
                          void requestSwitch(account)
                        }}
                      >
                        {account.needs_relogin ? t('switchAccount.needsRelogin') : t('switchAccount.switch')}
                      </Button>
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: [
                            {
                              key: 'remove',
                              danger: true,
                              label: t('switchAccount.remove'),
                              onClick: () => {
                                void onRemoveAccount(account)
                              },
                            },
                          ],
                        }}
                      >
                        <button
                          type="button"
                          className="gae-account-switch-more"
                          aria-label={t('switchAccount.moreActions')}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <MoreOutlined />
                        </button>
                      </Dropdown>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div className="gae-menu-footer" role="group" aria-label={t('switchAccount.footerGroup')}>
              <button type="button" className="gae-menu-footer-btn gae-menu-item-danger" onClick={() => void onLogout()}>
                <LogoutOutlined /> {t('menu.logout')}
              </button>
              <button
                type="button"
                className="gae-menu-footer-btn"
                data-testid="account-menu-add-account"
                onClick={openAddAccount}
              >
                <PlusOutlined /> {t('switchAccount.add')}
              </button>
            </div>
          </div>
        }
      >
        <button type="button" className="gae-avatar-trigger" aria-label={t('avatar.ariaLabel')}>
          <Avatar size={30} className="gae-avatar">
            {initialsFrom(user?.display_name, user?.email)}
          </Avatar>
        </button>
      </Popover>

      <AccountSwitchModal
        open={credentialOpen}
        mode={credentialMode}
        email={reauthEmail}
        onClose={() => {
          setCredentialOpen(false)
          setReauthUserId(null)
        }}
        onSuccess={(data) => void onCredentialSuccess(data)}
      />

      <Modal
        title={t('switchAccount.unsavedTitle')}
        open={canvasPromptOpen}
        onCancel={() => {
          setCanvasPromptOpen(false)
          setPendingUserId(null)
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setCanvasPromptOpen(false)
              setPendingUserId(null)
            }}
          >
            {t('switchAccount.unsavedCancel')}
          </Button>,
          <Button
            key="discard"
            danger
            onClick={() => {
              const userId = pendingUserId
              setCanvasPromptOpen(false)
              setPendingUserId(null)
              if (!userId) return
              void executeCanvasGuardedSwitch('discard', () => finishSwitch(userId))
            }}
          >
            {t('switchAccount.unsavedDiscard')}
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={() => {
              const userId = pendingUserId
              setCanvasPromptOpen(false)
              setPendingUserId(null)
              if (!userId) return
              void executeCanvasGuardedSwitch('save', () => finishSwitch(userId))
            }}
          >
            {t('switchAccount.unsavedSave')}
          </Button>,
        ]}
      >
        <p>{t('switchAccount.unsavedBody')}</p>
      </Modal>
    </div>
  )
}
